import React from 'react'
import { Box, Grid, Stack } from '@mui/material'
import { Skeleton } from '@repo/ui'

import {
  CERTIFICATE_GRID_SPACING,
  certificateGridItemSpan,
  type CertificatesLayout,
} from 'components/pages/certificates/CertificatesGrid'
import { surfaceRadius } from 'settings/brandRadius'

/**
 * Cards drawn on the first load — roughly one screenful at every column count.
 *
 * 6 divides cleanly by the 1 / 2 / 3 columns the grid actually renders, so the
 * last row is never a lone card hanging off a half-filled row, which is a shape
 * the real list only makes at the very end of a page.
 */
const PLACEHOLDER_CARDS = 6

export interface CertificatesGridSkeletonProps {
  /** Mirrors `CertificatesGrid`'s own prop — the phone column count follows it. */
  layout?: CertificatesLayout
  cards?: number
}

/**
 * Loading state for the certificates list (working-conventions §19).
 *
 * ## What this replaced, and why it counted as a violation
 *
 * A `Stack spacing={2}` of ten `h-40` bars, one per row, at every viewport.
 * Against the real grid that is wrong in three dimensions at once, and the
 * measurements are the argument:
 *
 * | | skeleton, before | the grid it stands in for |
 * |---|---|---|
 * | columns at `md` | 1 | **3** (`md={4}`) |
 * | columns at `sm` / grid-mode phone | 1 | **2** |
 * | card shape | a plain 160px bar | a bordered, 12px-radius, elevated card |
 * | count | 10 | a page of up to 12 |
 *
 * So on a desktop first load the page drew a single tall column and then
 * re-laid itself into three columns the moment data landed — the exact layout
 * shift §19 exists to prevent, and the most visible one left in this app.
 *
 * ## The columns are not restated here
 *
 * `certificateGridItemSpan` and `CERTIFICATE_GRID_SPACING` are imported from
 * `CertificatesGrid` rather than re-declared. Footprint parity that depends on
 * two files holding the same three breakpoints is parity until somebody edits
 * one of them; sourcing the span from the grid makes it structural, which is
 * the same move `ScansListSkeleton` makes with `LIST_ROW_LEADING_SIZE`.
 *
 * ## Inside the card
 *
 * The card CHROME is real — same `common.white` fill, same 12px radius, same
 * `grey[100]` hairline and the same two-layer shadow as `CertificatesTeaser` —
 * because that chrome is what gives the placeholder the card's true outer
 * dimensions. Only the contents are grey.
 *
 * The interior follows the same `compact` split the teaser makes:
 *
 * - **list** — a full-bleed 128px square on the leading edge, square on its
 *   inner corners, beside a text column, with the row's 112px `minHeight`.
 * - **grid** — a 1:1 tile at the card's full width with 12px of padding and the
 *   text stacked beneath it.
 *
 * No status chip and no activity line: both are conditional on the certificate
 * (the chip only renders for some states, the activity block is the card's whole
 * height variance), so reserving space for them on every placeholder would
 * itself be the shift.
 */
export const CertificatesGridSkeleton: React.FC<CertificatesGridSkeletonProps> = ({
  layout = 'list',
  cards = PLACEHOLDER_CARDS,
}) => {
  const compact = layout === 'grid'

  return (
    <Grid
      container
      spacing={CERTIFICATE_GRID_SPACING}
      aria-busy='true'
      aria-label='Loading certificates'
    >
      {Array.from({ length: cards }).map((_, index) => (
        <Grid item {...certificateGridItemSpan(layout)} key={index}>
          <Box
            sx={theme => ({
              background: theme.palette.common.white,
              // VRG-562: the skeleton must occupy the card's footprint exactly (§19),
              // so it follows the tenant's Corner radius along with the card.
              borderRadius: surfaceRadius(theme),
              border: `1px solid ${theme.palette.grey[100]}`,
              boxShadow:
                '0 1px 3px rgba(16, 24, 40, 0.10), 0 1px 2px rgba(16, 24, 40, 0.06)',
              overflow: 'hidden',
            })}
          >
            <Stack
              direction={compact ? 'column' : 'row'}
              alignItems='stretch'
              sx={{
                padding: compact ? '12px' : '0 12px 0 0',
                minHeight: compact ? undefined : '112px',
              }}
            >
              {/* The photo. Square in both modes; full-bleed with a squared
                  inner edge in list mode, exactly as the teaser draws it. */}
              <Skeleton
                variant='rectangular'
                sx={{
                  flexShrink: 0,
                  width: compact ? '100%' : '128px',
                  aspectRatio: '1 / 1',
                  height: 'auto',
                  alignSelf: compact ? 'stretch' : 'flex-start',
                  marginRight: compact ? 0 : '16px',
                  marginBottom: compact ? '10px' : 0,
                  borderRadius: compact ? '8px' : '12px 0 0 12px',
                }}
              />

              {/* Title, then the shorter metadata line under it. `minWidth: 0`
                  so the bars are measured against the column rather than
                  pushing it wider than the card. */}
              <Stack
                justifyContent='center'
                spacing={1}
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  paddingY: compact ? 0 : '12px',
                }}
              >
                <Skeleton variant='text' sx={{ width: '70%', height: 20 }} />
                <Skeleton variant='text' sx={{ width: '45%', height: 16 }} />
              </Stack>
            </Stack>
          </Box>
        </Grid>
      ))}
    </Grid>
  )
}

export default CertificatesGridSkeleton
